// One cold point's settings history: who changed what, when and why (newest first; an archive is the newest event).
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { coldChain } from '@/lib/trackizy';
import { showToast } from '@/components/shared/Toast';
import { dateTimeInTz } from '@/features/staff/attendanceModel';
import { changeLines, entryTitle, assumedNote } from './coldPointHistoryModel';

export default function ColdPointHistoryTab({ pointId, tz }) {
  const { t } = useTranslation();
  const [entries, setEntries] = useState(null);
  const loadSeq = useRef(0);

  useEffect(() => {
    const seq = ++loadSeq.current;
    setEntries(null);
    coldChain.versions(pointId)
      .then((r) => { if (seq === loadSeq.current) setEntries(Array.isArray(r) ? r : (r?.versions ?? [])); })
      .catch((e) => {
        if (seq !== loadSeq.current) return;   // a newer load (or unmount) owns the state now
        setEntries([]);
        showToast(e?.message || t('coldChain.history.loadError', 'Could not load the settings history'));
      });
    return () => { loadSeq.current += 1; };
  }, [pointId, t]);

  if (!entries) return <Loader2 size={16} className="animate-spin text-slate-400" />;
  if (!entries.length) return <p className="text-sm text-slate-500">{t('coldChain.history.empty', 'No settings history yet')}</p>;

  return (
    <ul className="divide-y divide-slate-100 dark:divide-slate-800">
      {entries.map((e, i) => {
        const archive = e.kind === 'archive';
        const by = archive ? e.archivedByEmail : e.changedByEmail;
        const reason = archive ? e.archiveReason : e.reason;
        const note = assumedNote(e, t);
        return (
          <li key={archive ? 'archive' : (e.versionNo ?? i)} className="space-y-1 py-2.5 text-sm">
            <div className="flex flex-wrap items-baseline gap-x-2 text-slate-800 dark:text-slate-100">
              <span className="font-semibold">{entryTitle(e, t)}</span>
              <span className="text-xs text-slate-500"><bdi>{dateTimeInTz(archive ? e.archivedAt : e.effectiveFrom, tz)}</bdi></span>
              {by && <span className="text-xs text-slate-500">{t('coldChain.history.by', 'by')} <bdi>{by}</bdi></span>}
            </div>
            {changeLines(e, t).map((line) => (
              <p key={line} className="text-slate-700 dark:text-slate-200"><bdi>{line}</bdi></p>
            ))}
            {reason && <p className="text-xs italic text-slate-500">{'“'}<bdi>{reason}</bdi>{'”'}</p>}
            {note && <p className="text-xs text-amber-600 dark:text-amber-400">{note}</p>}
          </li>
        );
      })}
    </ul>
  );
}
